import React, { useState, useEffect, useRef } from "react";
import { NotificationBanner } from '../NotificationBanner';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Badge,
  Spinner,
  Tooltip,
} from "@fluentui/react-components";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DocumentRegular,
  GlobeRegular,
  SettingsRegular,
  SettingsFilled,
  DocumentTextRegular,
  DocumentTextFilled,
  DocumentBulletListRegular,
  ChevronRightRegular,
  ChevronLeftRegular,
  CheckmarkCircle16Filled,
  PlugDisconnected16Regular,
  Warning16Filled,
} from "@fluentui/react-icons";
import { Header } from "./Header";
import {
  useUnifiedApp,
  useDashboardStats,
} from "../../contexts/UnifiedAppContext";
import {
  versionService,
  type VersionInfo,
} from "../../services/versionService";
import UsageTracking from "../../tracking";
import useEventTracker from "../../hooks/useEventTracker";
import { ElementButton, PageName } from "../../tracking/tracking.types";
import { useNavigationGuard } from "../../contexts/NavigationGuardContext";
import {
  useDisclaimerEnrollmentSummary,
  type EnrollmentStatusLoadState,
} from "../../contexts/DisclaimerEnrollmentSummaryContext";
import { useGeneralConfig } from "../../hooks/useGeneralConfig";
import { CUSTOM_DISCLAIMERS_REQUIRED_PERMISSIONS } from "../../constants/customDisclaimers";

interface UnifiedLayoutProps {
  children: React.ReactNode;
}

const DISCLAIMER_PERMISSION_FOCUS_REFRESH_INTERVAL_MS = 1000;

interface NavigationItem {
  key: string;
  label: string;
  path: string;
  icon: React.ReactElement;
  iconFilled: React.ReactElement;
  badge?: number;
  elementButton: ElementButton;
}

const navigationItems: NavigationItem[] = [
  {
    key: "logs",
    label: "Logs",
    path: "/logs",
    icon: <DocumentTextRegular />,
    iconFilled: <DocumentTextFilled />,
    elementButton: ElementButton.LOGS,
  },
  {
    key: "settings",
    label: "Settings",
    path: "/settings",
    icon: <SettingsRegular />,
    iconFilled: <SettingsFilled />,
    elementButton: ElementButton.SETTINGS,
  },
];

const useStyles = makeStyles({
  layout: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100vw",
    maxWidth: "100%",
    backgroundColor: "#f5f5f5",
    overflow: "hidden",
    position: "relative",
  },
  main: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    width: "100%",
    maxWidth: "100%",
    minHeight: 0,
  },
  sidebar: {
    width: "320px",
    height: "100vh",
    backgroundColor: "#ffffff",
    position: "fixed",
    left: 0,
    top: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.08)",
    zIndex: 1000,
    transition: "width 0.3s ease",
  },
  sidebarCollapsed: {
    width: "72px",
  },
  brandSection: {
    height: "72px",
    display: "flex",
    alignItems: "center",
    ...shorthands.padding("0", "24px"),
    ...shorthands.borderBottom("1px", "solid", "#f0f0f0"),
    backgroundColor: "#ffffff",
  },
  brandSectionCollapsed: {
    ...shorthands.padding("0", "16px"),
    justifyContent: "center",
  },
  brandContent: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("12px"),
    flex: 1,
  },
  logo: {
    height: "42px",
    width: "auto",
    flexShrink: 0,
  },
  brandInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  brandTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#1a1a1a",
    lineHeight: "1.2",
    margin: 0,
  },
  brandSubtitle: {
    fontSize: "12px",
    color: "#666666",
    lineHeight: "1.2",
    margin: 0,
  },
  brandVersion: {
    fontSize: "10px",
    color: "#999999",
    margin: 0,
    fontWeight: "400",
    fontFamily: "Consolas, Monaco, monospace",
    lineHeight: "1.2",
  },
  collapseButton: {
    minWidth: "32px",
    minHeight: "32px",
    ...shorthands.padding("4px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "#e0e0e0"),
    ...shorthands.borderRadius("6px"),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  cardsSection: {
    ...shorthands.padding("20px", "16px"),
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("12px"),
  },
  cardsSectionCollapsed: {
    ...shorthands.padding("20px", "8px"),
  },
  summaryCard: {
    width: "100%",
    backgroundColor: "#ffffff",
    ...shorthands.border("1px", "solid", "#e8e8e8"),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
    textAlign: "left",
    padding: 0,
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      ...shorthands.borderRadius("12px"),
      ...shorthands.border("2px", "solid", "#0066cc"),
      opacity: 0,
      transform: "scale(0.95)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      pointerEvents: "none",
    },
    "&:hover": {
      boxShadow: "0 8px 20px rgba(0, 102, 204, 0.12)",
      transform: "translateY(-2px)",
      ...shorthands.borderColor("#0066cc"),
    },
    "&:focus-visible": {
      boxShadow: "0 8px 20px rgba(0, 102, 204, 0.12)",
      transform: "translateY(-2px)",
      ...shorthands.borderColor("#0066cc"),
      outline: `2px solid #0066cc`,
      outlineOffset: "2px",
    },
    "&:focus-visible::after": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
  summaryCardCollapsed: {
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  disclaimerSummaryCard: {
    minHeight: "134px",
  },
  cardHeader: {
    ...shorthands.padding("16px", "16px", "8px", "16px"),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  cardTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1a1a1a",
    margin: 0,
  },
  cardBadge: {
    fontSize: "11px",
    fontWeight: "600",
  },
  cardBody: {
    ...shorthands.padding("0", "16px", "16px", "16px"),
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("8px"),
  },
  cardMetric: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#0066cc",
    lineHeight: "1.2",
    margin: 0,
  },
  cardDescription: {
    fontSize: "12px",
    color: "#666666",
    lineHeight: "1.3",
    margin: 0,
  },
  disclaimerStatusInline: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: "#616161",
    whiteSpace: "nowrap",
  },
  navigationSection: {
    flex: 1,
    ...shorthands.padding("8px", "12px"),
    overflowY: "auto",
  },
  navigationSectionCollapsed: {
    ...shorthands.padding("8px", "4px"),
  },
  navItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    ...shorthands.padding("12px"),
    ...shorthands.margin("0", "0", "4px", "0"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#666666",
    ...shorthands.gap("12px"),
    transition: "all 0.2s ease",
    textAlign: "left",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      color: "#0066cc",
    },
  },
  navItemCollapsed: {
    justifyContent: "center",
    ...shorthands.padding("12px", "8px"),
    minHeight: "44px",
  },
  titleText: {
    flexDirection: "column",
    rowGap: "0px",
    paddingLeft: "0px",
    paddingRight: "14px",
  },
  titleVersionContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  navItemActive: {
    backgroundColor: "#e6f3ff",
    color: "#0066cc",
    fontWeight: "600",
    "&:hover": {
      backgroundColor: "#e6f3ff",
      color: "#0066cc",
    },
  },
  navIcon: {
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  navLabel: {
    flex: 1,
  },
  content: {
    flex: 1,
    marginLeft: "320px",
    backgroundColor: "#f5f5f5",
    transition: "margin-left 0.3s ease",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    width: "calc(100% - 320px)",
    maxWidth: "calc(100% - 320px)",
    overflow: "hidden",
    minWidth: 0,
  },
  contentCollapsed: {
    marginLeft: "72px",
    width: "calc(100% - 72px)",
    maxWidth: "calc(100% - 72px)",
  },
  contentFullWidth: {
    marginLeft: "0",
    width: "100%",
    maxWidth: "100%",
  },
  header: {
    height: "64px",
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    ...shorthands.padding("0", "24px"),
    ...shorthands.borderBottom("1px", "solid", "#e8e8e8"),
    flexShrink: 0,
  },
  contentInner: {
    flex: 1,
    ...shorthands.padding("24px"),
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minWidth: 0, // Critical for flex children to properly shrink
  },
  domainUsageIndicator: {
    fontSize: "11px",
    color: "#666666",
    marginTop: "4px",
  },
  statusSection: {
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderTop("1px", "solid", "#f0f0f0"),
    marginBottom: "16px",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
  },
  statusSectionCollapsed: {
    ...shorthands.padding("8px", "4px"),
    alignItems: "center",
  },
  statusItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("10px", "12px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.gap("4px"),
  },
  statusItemCollapsed: {
    justifyContent: "center",
    ...shorthands.padding("10px", "8px"),
    minHeight: "44px",
  },
  statusLabel: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#666666",
  },
  statusValue: {
    fontSize: "13px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("4px"),
  },
  statusConnected: {
    color: "#0e700e",
    backgroundColor: "#e7f4e7",
  },
  statusDisconnected: {
    color: "#c4314b",
    backgroundColor: "#fdf2f4",
  },
  statusLoading: {
    color: "#666666",
    backgroundColor: "#f0f0f0",
  },
});

type StatusType = "connected" | "loading" | "disconnected";

const getStatusLabel = (status: StatusType): string => {
  if (status === "loading") return "Checking...";
  if (status === "connected") return "Connected";
  return "Disconnected";
};

const renderStatusIcon = (status: StatusType): React.ReactElement => {
  if (status === "loading") return <Spinner size="extra-tiny" />;
  if (status === "connected") return <CheckmarkCircle16Filled />;
  return <PlugDisconnected16Regular />;
};

const getStatusStyle = (
  status: StatusType,
  styles: ReturnType<typeof useStyles>,
): string => {
  if (status === "connected") return styles.statusConnected;
  if (status === "loading") return styles.statusLoading;
  return styles.statusDisconnected;
};

interface UnifiedSidebarProps {
  styles: ReturnType<typeof useStyles>;
  isCollapsed: boolean;
  pathname: string;
  version: VersionInfo | null;
  dashboardStats: ReturnType<typeof useDashboardStats>;
  onToggleCollapse: () => void;
  onNavigate: (path: string, elementButton: ElementButton) => void;
  disclaimerListState: EnrollmentStatusLoadState;
  disclaimerCount: number | null;
  assignedDisclaimerCount: number | null;
  unassignedDisclaimerCount: number | null;
  enrollmentState: EnrollmentStatusLoadState;
  organizationEnrolledUserCount: number | null;
  disclaimerPermissionState: DisclaimerPermissionState;
}

type DisclaimerPermissionState = "checking" | "granted" | "required" | "unknown";

const numberFormatter = new Intl.NumberFormat("en-US");

const formatCount = (value: number | null): string => numberFormatter.format(value ?? 0);

const pluralize = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

const renderDisclaimerMetric = (
  styles: ReturnType<typeof useStyles>,
  permissionState: DisclaimerPermissionState,
  disclaimerListState: EnrollmentStatusLoadState,
  disclaimerCount: number | null,
): React.ReactNode => {
  if (permissionState === "required") {
    return "0";
  }
  if (permissionState === "checking") {
    return (
      <span className={styles.disclaimerStatusInline}>
        <Spinner
          size="extra-tiny"
          label="Refreshing disclaimers"
          data-testid="sidebar-summary-disclaimers-loading-spinner"
        />
      </span>
    );
  }
  if (permissionState === "unknown") {
    return <span aria-label="Disclaimer permission status unavailable">—</span>;
  }
  if (disclaimerListState === "loading") {
    return (
      <span className={styles.disclaimerStatusInline}>
        <Spinner
          size="extra-tiny"
          label="Refreshing disclaimers"
          data-testid="sidebar-summary-disclaimers-loading-spinner"
        />
      </span>
    );
  }
  if (disclaimerListState === "error") {
    return <span aria-label="Disclaimer count unavailable">—</span>;
  }
  return formatCount(disclaimerCount);
};

const getDisclaimerAssignmentText = (
  permissionState: DisclaimerPermissionState,
  disclaimerListState: EnrollmentStatusLoadState,
  disclaimerCount: number | null,
  assignedDisclaimerCount: number | null,
  unassignedDisclaimerCount: number | null,
): string | null => {
  if (permissionState !== "granted" || disclaimerListState !== "ready" || !disclaimerCount) {
    return null;
  }
  return `${formatCount(assignedDisclaimerCount)} assigned • ${formatCount(unassignedDisclaimerCount)} unassigned`;
};

const renderDisclaimerDescription = (
  styles: ReturnType<typeof useStyles>,
  permissionState: DisclaimerPermissionState,
  disclaimerListState: EnrollmentStatusLoadState,
  enrollmentState: EnrollmentStatusLoadState,
  organizationEnrolledUserCount: number | null,
): React.ReactNode => {
  if (permissionState === "checking" || disclaimerListState === "loading") return null;
  if (permissionState === "required") {
    return (
      <span className={styles.disclaimerStatusInline}>
        <Warning16Filled />
        Permissions required
      </span>
    );
  }
  if (permissionState === "unknown") return "Permission status unavailable";
  if (enrollmentState === "loading") return "Loading enrollment status";
  if (enrollmentState === "error") return "Enrollment status unavailable";
  const count = organizationEnrolledUserCount ?? 0;
  return `${formatCount(count)} ${pluralize(count, "user", "users")} enrolled`;
};

const getDisclaimerCollapsedTitle = (
  permissionState: DisclaimerPermissionState,
  disclaimerListState: EnrollmentStatusLoadState,
  disclaimerCount: number | null,
): string => {
  if (permissionState === "checking") return "Disclaimers - checking permissions";
  if (permissionState === "required") return "Disclaimers - permissions required";
  if (permissionState === "unknown") return "Disclaimers - permission status unavailable";
  if (disclaimerListState === "loading") return "Disclaimers - loading";
  if (disclaimerListState === "error") return "Disclaimers - unavailable";
  const count = disclaimerCount ?? 0;
  return `Disclaimers - ${formatCount(count)} ${pluralize(count, "disclaimer", "disclaimers")} total`;
};

const getDisclaimerAriaLabel = (
  permissionState: DisclaimerPermissionState,
  disclaimerListState: EnrollmentStatusLoadState,
  disclaimerCount: number | null,
): string => {
  if (permissionState === "checking") return "Navigate to disclaimers page - checking permissions";
  if (permissionState === "required") return "Navigate to disclaimers page - permissions required";
  if (permissionState === "unknown") return "Navigate to disclaimers page - permission status unavailable";
  if (disclaimerListState === "loading") return "Navigate to disclaimers page - loading";
  if (disclaimerListState === "error") return "Navigate to disclaimers page - unavailable";
  const count = disclaimerCount ?? 0;
  return `Navigate to disclaimers page - ${formatCount(count)} ${pluralize(count, "disclaimer", "disclaimers")} total`;
};

interface SidebarSectionProps {
  styles: ReturnType<typeof useStyles>;
  isCollapsed: boolean;
  dashboardStats: ReturnType<typeof useDashboardStats>;
  onNavigate: (path: string, elementButton: ElementButton) => void;
  disclaimerListState: EnrollmentStatusLoadState;
  disclaimerCount: number | null;
  assignedDisclaimerCount: number | null;
  unassignedDisclaimerCount: number | null;
  enrollmentState: EnrollmentStatusLoadState;
  organizationEnrolledUserCount: number | null;
  disclaimerPermissionState: DisclaimerPermissionState;
}

const SidebarSummarySection: React.FC<SidebarSectionProps> = ({
  styles,
  isCollapsed,
  dashboardStats,
  onNavigate,
  disclaimerListState,
  disclaimerCount,
  assignedDisclaimerCount,
  unassignedDisclaimerCount,
  enrollmentState,
  organizationEnrolledUserCount,
  disclaimerPermissionState,
}) => {
  const disclaimerAssignmentText = getDisclaimerAssignmentText(
    disclaimerPermissionState,
    disclaimerListState,
    disclaimerCount,
    assignedDisclaimerCount,
    unassignedDisclaimerCount,
  );
  const disclaimerDescription = renderDisclaimerDescription(
    styles,
    disclaimerPermissionState,
    disclaimerListState,
    enrollmentState,
    organizationEnrolledUserCount,
  );

  return (
  <div
    className={mergeClasses(
      styles.cardsSection,
      isCollapsed && styles.cardsSectionCollapsed,
    )}
    data-testid="sidebar-summary-section"
  >
    <button
      className={mergeClasses(
        styles.summaryCard,
        isCollapsed && styles.summaryCardCollapsed,
      )}
      onClick={() => onNavigate("/policies", ElementButton.POLICIES)}
      title={
        isCollapsed
          ? `Policies - ${dashboardStats.totalPolicies} total`
          : undefined
      }
      type="button"
      aria-label={`Navigate to policies page - ${dashboardStats.totalPolicies} policies total`}
      data-testid="sidebar-summary-policies-card"
    >
      {isCollapsed ? (
        <DocumentRegular
          style={{ fontSize: "20px", color: "#0066cc" }}
          data-testid="sidebar-summary-policies-icon"
        />
      ) : (
        <>
          <div
            className={styles.cardHeader}
            data-testid="sidebar-summary-policies-header"
          >
            <h3
              className={styles.cardTitle}
              data-testid="sidebar-summary-policies-title"
            >
              Policies
            </h3>
            {dashboardStats.policiesWithDrift > 0 && (
              <Badge
                appearance="filled"
                color="danger"
                size="small"
                className={styles.cardBadge}
                data-testid="sidebar-summary-policies-badge"
              >
                {dashboardStats.policiesWithDrift} issue
              </Badge>
            )}
          </div>
          <div
            className={styles.cardBody}
            data-testid="sidebar-summary-policies-body"
          >
            <div
              className={styles.cardMetric}
              data-testid="sidebar-summary-policies-metric"
            >
              {dashboardStats.totalPolicies}
            </div>
            <div
              className={styles.cardDescription}
              data-testid="sidebar-summary-policies-description"
            >
              {dashboardStats.managedPolicies} managed •{" "}
              {dashboardStats.unmanagedPolicies} unmanaged
            </div>
          </div>
        </>
      )}
    </button>

    <button
      className={mergeClasses(
        styles.summaryCard,
        isCollapsed && styles.summaryCardCollapsed,
      )}
      onClick={() => onNavigate("/domains", ElementButton.DOMAINS)}
      title={
        isCollapsed
          ? `Domains - ${dashboardStats.totalDomains} total`
          : undefined
      }
      type="button"
      aria-label={`Navigate to domains page - ${dashboardStats.totalDomains} domains total`}
      data-testid="sidebar-summary-domains-card"
    >
      {isCollapsed ? (
        <GlobeRegular
          style={{ fontSize: "20px", color: "#0066cc" }}
          data-testid="sidebar-summary-domains-icon"
        />
      ) : (
        <>
          <div
            className={styles.cardHeader}
            data-testid="sidebar-summary-domains-header"
          >
            <h3
              className={styles.cardTitle}
              data-testid="sidebar-summary-domains-title"
            >
              Domains
            </h3>
            {dashboardStats.pendingDomains > 0 && (
              <Badge
                appearance="filled"
                color="warning"
                size="small"
                className={styles.cardBadge}
                data-testid="sidebar-summary-domains-badge"
              >
                {dashboardStats.pendingDomains} pending
              </Badge>
            )}
          </div>
          <div
            className={styles.cardBody}
            data-testid="sidebar-summary-domains-body"
          >
            <div
              className={styles.cardMetric}
              data-testid="sidebar-summary-domains-metric"
            >
              {dashboardStats.totalDomains}
            </div>
            <div
              className={styles.cardDescription}
              data-testid="sidebar-summary-domains-description"
            >
              {dashboardStats.approvedDomains} approved •{" "}
              {dashboardStats.pendingDomains || 0} pending
            </div>
          </div>
        </>
      )}
    </button>

    <button
      className={mergeClasses(
        styles.summaryCard,
        !isCollapsed && styles.disclaimerSummaryCard,
        isCollapsed && styles.summaryCardCollapsed,
      )}
      onClick={() =>
        onNavigate("/custom-disclaimers", ElementButton.CUSTOM_DISCLAIMERS)
      }
      title={
        isCollapsed
          ? getDisclaimerCollapsedTitle(disclaimerPermissionState, disclaimerListState, disclaimerCount)
          : undefined
      }
      type="button"
      aria-label={getDisclaimerAriaLabel(disclaimerPermissionState, disclaimerListState, disclaimerCount)}
      data-testid="sidebar-summary-disclaimers-card"
    >
      {isCollapsed ? (
        <DocumentBulletListRegular
          style={{ fontSize: "20px", color: "#0066cc" }}
          data-testid="sidebar-summary-disclaimers-icon"
        />
      ) : (
        <>
          <div
            className={styles.cardHeader}
            data-testid="sidebar-summary-disclaimers-header"
          >
            <h3
              className={styles.cardTitle}
              data-testid="sidebar-summary-disclaimers-title"
            >
              Disclaimers
            </h3>
          </div>
          <div
            className={styles.cardBody}
            data-testid="sidebar-summary-disclaimers-body"
          >
            <div
              className={styles.cardMetric}
              data-testid="sidebar-summary-disclaimers-metric"
            >
              {renderDisclaimerMetric(styles, disclaimerPermissionState, disclaimerListState, disclaimerCount)}
            </div>
            {disclaimerAssignmentText && (
              <div
                className={styles.cardDescription}
                data-testid="sidebar-summary-disclaimers-assignment"
              >
                {disclaimerAssignmentText}
              </div>
            )}
            {disclaimerDescription && (
              <div
                className={styles.cardDescription}
                data-testid="sidebar-summary-disclaimers-description"
              >
                {disclaimerDescription}
              </div>
            )}
          </div>
        </>
      )}
    </button>
  </div>
  );
};

const SidebarStatusSection: React.FC<
  Pick<SidebarSectionProps, "styles" | "isCollapsed" | "dashboardStats">
> = ({ styles, isCollapsed, dashboardStats }) => {
  const statusItems = [
    {
      key: "api-feed",
      label: "API Feed:",
      tooltipPrefix: "API Feed",
      status: dashboardStats.systemStatus.lsegFeed,
      testIdPrefix: "sidebar-status-api-feed",
    },
    {
      key: "teams-status",
      label: "Teams Admin Center status:",
      tooltipPrefix: "Teams Admin Center",
      status: dashboardStats.systemStatus.teamsStatus,
      testIdPrefix: "sidebar-status-teams-status",
    },
  ];

  return (
    <div
      className={mergeClasses(
        styles.statusSection,
        isCollapsed && styles.statusSectionCollapsed,
      )}
      data-testid="sidebar-status-section"
    >
      {statusItems.map((item) => {
        const statusLabel = getStatusLabel(item.status);
        const statusContent = (
          <div
            className={mergeClasses(
              styles.statusItem,
              isCollapsed && styles.statusItemCollapsed,
            )}
            data-testid={item.testIdPrefix}
          >
            {!isCollapsed && (
              <span
                className={styles.statusLabel}
                data-testid={`${item.testIdPrefix}-label`}
              >
                {item.label}
              </span>
            )}
            <span
              className={mergeClasses(
                styles.statusValue,
                getStatusStyle(item.status, styles),
              )}
              data-testid={`${item.testIdPrefix}-value`}
            >
              {renderStatusIcon(item.status)}
              {!isCollapsed && statusLabel}
            </span>
          </div>
        );

        return isCollapsed ? (
          <Tooltip
            key={item.key}
            content={`${item.tooltipPrefix} ${statusLabel.toLowerCase()}`}
            relationship="label"
            positioning="after"
            data-testid={`${item.testIdPrefix}-tooltip`}
          >
            {statusContent}
          </Tooltip>
        ) : (
          <React.Fragment key={item.key}>{statusContent}</React.Fragment>
        );
      })}
    </div>
  );
};

const UnifiedSidebar: React.FC<UnifiedSidebarProps> = ({
  styles,
  isCollapsed,
  pathname,
  version,
  dashboardStats,
  onToggleCollapse,
  onNavigate,
  disclaimerListState,
  disclaimerCount,
  assignedDisclaimerCount,
  unassignedDisclaimerCount,
  enrollmentState,
  organizationEnrolledUserCount,
  disclaimerPermissionState,
}) => (
  <aside
    className={mergeClasses(
      styles.sidebar,
      isCollapsed && styles.sidebarCollapsed,
    )}
    data-testid="unified-sidebar"
  >
    <div
      className={mergeClasses(
        styles.brandSection,
        isCollapsed && styles.brandSectionCollapsed,
      )}
      data-testid="unified-sidebar-brand-section"
    >
      {!isCollapsed && (
        <div
          className={mergeClasses(styles.brandContent, styles.titleText)}
          data-testid="unified-sidebar-brand-content"
        >
          <h4 data-testid="unified-sidebar-brand-title">
            Automated Domain Management
          </h4>
          <div
            className={styles.titleVersionContainer}
            data-testid="unified-sidebar-brand-title-version-row"
          >
            <h4 data-testid="unified-sidebar-brand-title-adm">(ADM)</h4>
            {version?.currentVersion?.armVersion && (
              <span
                className={styles.brandVersion}
                data-testid="unified-sidebar-brand-version"
              >
                v{version.currentVersion.armVersion}
              </span>
            )}
          </div>
        </div>
      )}
      <button
        className={styles.collapseButton}
        onClick={onToggleCollapse}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        data-testid="sidebar-collapse-toggle"
      >
        {isCollapsed ? <ChevronRightRegular /> : <ChevronLeftRegular />}
      </button>
    </div>

    <SidebarSummarySection
      styles={styles}
      isCollapsed={isCollapsed}
      dashboardStats={dashboardStats}
      onNavigate={onNavigate}
      disclaimerListState={disclaimerListState}
      disclaimerCount={disclaimerCount}
      assignedDisclaimerCount={assignedDisclaimerCount}
      unassignedDisclaimerCount={unassignedDisclaimerCount}
      enrollmentState={enrollmentState}
      organizationEnrolledUserCount={organizationEnrolledUserCount}
      disclaimerPermissionState={disclaimerPermissionState}
    />

    <div
      className={mergeClasses(
        styles.navigationSection,
        isCollapsed && styles.navigationSectionCollapsed,
      )}
      data-testid="sidebar-navigation-section"
    >
      {navigationItems.map((item, index) => {
        const isActive = pathname === item.path;

        return (
          <button
            key={item.key}
            className={mergeClasses(
              styles.navItem,
              isCollapsed && styles.navItemCollapsed,
              isActive && styles.navItemActive,
            )}
            onClick={() => onNavigate(item.path, item.elementButton)}
            title={isCollapsed ? item.label : undefined}
            data-testid={`sidebar-navigation-item-${index}`}
          >
            <span
              className={styles.navIcon}
              data-testid={`sidebar-navigation-item-${index}-icon`}
            >
              {isActive ? item.iconFilled : item.icon}
            </span>
            {!isCollapsed && (
              <span
                className={styles.navLabel}
                data-testid={`sidebar-navigation-item-${index}-label`}
              >
                {item.label}
              </span>
            )}
          </button>
        );
      })}
    </div>

    <SidebarStatusSection
      styles={styles}
      isCollapsed={isCollapsed}
      dashboardStats={dashboardStats}
    />
  </aside>
);

export const UnifiedLayout: React.FC<UnifiedLayoutProps> = ({ children }) => {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [version, setVersion] = useState<VersionInfo | null>(null);

  // Check if we're on the onboarding page
  const isOnboardingPage = location.pathname === "/onboarding";

  // Use context for real data
  const { setCurrentView } = useUnifiedApp();
  const dashboardStats = useDashboardStats();
  const {
    disclaimerState: disclaimerListState,
    disclaimerCount,
    assignedDisclaimerCount,
    unassignedDisclaimerCount,
    enrollmentState,
    organizationEnrolledUserCount,
  } = useDisclaimerEnrollmentSummary();
  const {
    config: generalConfig,
    loadingConfig: checkingDisclaimerPermission,
    loadConfig: loadGeneralConfig,
  } = useGeneralConfig();
  const lastDisclaimerPermissionFocusRefreshRef = useRef(0);

  useEffect(() => {
    loadGeneralConfig();

    const refreshPermissionState = () => {
      const now = Date.now();
      if (
        now - lastDisclaimerPermissionFocusRefreshRef.current <
        DISCLAIMER_PERMISSION_FOCUS_REFRESH_INTERVAL_MS
      ) {
        return;
      }
      lastDisclaimerPermissionFocusRefreshRef.current = now;
      loadGeneralConfig();
    };

    globalThis.addEventListener("focus", refreshPermissionState);
    return () => globalThis.removeEventListener("focus", refreshPermissionState);
  }, [loadGeneralConfig]);

  const grantedPermissions = new Set(
    (generalConfig?.applications?.backend?.azure?.apiPermissions ?? []).map((permission) =>
      permission.toLowerCase(),
    ),
  );
  const hasDisclaimerPermission = CUSTOM_DISCLAIMERS_REQUIRED_PERMISSIONS.every((permission) =>
    grantedPermissions.has(permission.toLowerCase()),
  );
  let disclaimerPermissionState: DisclaimerPermissionState = "unknown";
  if (checkingDisclaimerPermission) {
    disclaimerPermissionState = "checking";
  } else if (generalConfig) {
    disclaimerPermissionState = hasDisclaimerPermission ? "granted" : "required";
  }

  // Update current view when route changes
  React.useEffect(() => {
    const path = location.pathname.replace("/", "") || "dashboard";
    setCurrentView(path);
  }, [location.pathname, setCurrentView]);

  // Fetch version information
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const versionInfo = await versionService.getVersion();
        setVersion(versionInfo);
        if (versionInfo?.currentVersion?.backendVersion && versionInfo?.currentVersion?.armVersion) {
          UsageTracking.Instance.setVersionInfo(
            versionInfo.currentVersion.backendVersion,
            versionInfo.currentVersion.armVersion,
          );
        }
      } catch (error) {
        console.warn("Failed to fetch version info:", error);
      }
    };

    fetchVersion();
  }, []);

  const eventTracker = useEventTracker();
  const { requestNavigation } = useNavigationGuard();
  const trackMenuAction = async (elementButton: ElementButton) => {
    await eventTracker.eventTrackOnElementClicked({
      elementName: elementButton,
      pageName: PageName.MENU,
    });
  };

  const handleNavigation = (path: string, elementButton: ElementButton) => {
    // While a save is in progress this prompts the user with a leave/stay dialog
    // and only navigates if they confirm.
    requestNavigation(() => {
      navigate(path);
      void trackMenuAction(elementButton);
    });
  };

  const handleSidebarToggle = () => {
    void trackMenuAction(
      isCollapsed ? ElementButton.EXPAND : ElementButton.COLLAPSE,
    );
    setIsCollapsed((previousValue) => !previousValue);
  };

  return (
    <div className={styles.layout} data-testid="unified-layout-root">
      <div className={styles.main} data-testid="unified-layout-main">
        {/* Unified Sidebar - Hidden on onboarding page */}
        {!isOnboardingPage && (
          <UnifiedSidebar
            styles={styles}
            isCollapsed={isCollapsed}
            pathname={location.pathname}
            version={version}
            dashboardStats={dashboardStats}
            onToggleCollapse={handleSidebarToggle}
            onNavigate={handleNavigation}
            disclaimerListState={disclaimerListState}
            disclaimerCount={disclaimerCount}
            assignedDisclaimerCount={assignedDisclaimerCount}
            unassignedDisclaimerCount={unassignedDisclaimerCount}
            enrollmentState={enrollmentState}
            organizationEnrolledUserCount={organizationEnrolledUserCount}
            disclaimerPermissionState={disclaimerPermissionState}
          />
        )}
        {/* Main Content Area */}
        <main
          className={mergeClasses(
            styles.content,
            isCollapsed && !isOnboardingPage && styles.contentCollapsed,
            isOnboardingPage && styles.contentFullWidth,
          )}
          data-testid="unified-layout-main-content"
        >
          <div className={styles.header} data-testid="main-header">
            <Header />
          </div>
          <div
            className={styles.contentInner}
            data-testid="unified-layout-content-inner"
          >
            <div
              style={{
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              }}
              data-testid="unified-layout-page-container"
            >
              {/* <DriftNotificationBanner
                onShowDetails={handleShowDriftDetails}
                refreshInterval={30000}
                refreshTrigger={refreshTrigger}
              /> */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
                data-testid="unified-layout-page-content"
              >
                <NotificationBanner />
                {children}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Drift dialog removed - using /drift-test page instead */}
    </div>
  );
};
